/**
 * Campana de notificaciones para el Topbar.
 * - Badge rojo con la cantidad de no-leídas.
 * - Dropdown con las últimas notificaciones.
 * - Click en una notif: la marca como leída y navega al link si existe.
 * - Botón "Marcar todas como leídas" y "Limpiar todo".
 */
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Bell, CheckCircle2, AlertCircle, Info, Check, Circle, Inbox } from "lucide-react"
import { useNotifications } from "./NotificationContext"

const TIPO_META = {
  success: { icon: CheckCircle2, cls: "text-success", bg: "bg-success/10" },
  error:   { icon: AlertCircle,  cls: "text-danger",  bg: "bg-danger/10" },
  info:    { icon: Info,         cls: "text-brand-600", bg: "bg-brand-50" },
}

function haceCuanto(ts) {
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return "ahora"
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`
  return `hace ${Math.floor(s / 86400)} d`
}

export default function NotificationBell() {
  const nav = useNavigate()
  const {
    items, unreadCount, loading,
    markAsRead, toggleRead, markAllAsRead,
  } = useNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Cerrar al click afuera
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  function handleClick(n) {
    // Si tiene link y no fue leida -> navegar al flujo
    if (n.link && !n.leida) {
      markAsRead(n.id)
      setOpen(false)
      nav(n.link)
    } else {
      // Si no -> toggle leido/no-leido
      toggleRead(n.id)
    }
  }

  const visibles = items.slice(0, 12)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-surface-100 transition-colors"
        title="Notificaciones"
        aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ""}`}
      >
        <Bell className="w-[18px] h-[18px] text-surface-600" strokeWidth={2.2} />
        {unreadCount > 0 && (
          <motion.span
            key={unreadCount}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center shadow-card tabular-nums"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-surface-200 shadow-float overflow-hidden z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100">
              <div>
                <div className="text-sm font-bold text-surface-900">Notificaciones</div>
                <div className="text-[10px] text-surface-400 uppercase tracking-wider font-semibold">
                  {items.length === 0 ? "Sin actividad" : `${items.length} en total`}
                </div>
              </div>

            </div>

            {/* Lista */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading && items.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <div className="w-6 h-6 mx-auto border-2 border-brand/30 border-t-brand rounded-full animate-spin mb-3" />
                  <div className="text-xs text-surface-400">Cargando notificaciones...</div>
                </div>
              ) : visibles.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                    <Inbox className="w-6 h-6 text-surface-400" strokeWidth={1.5} />
                  </div>
                  <div className="text-sm font-semibold text-surface-700">Sin notificaciones</div>
                  <div className="text-xs text-surface-400 mt-1">
                    Acá vas a ver los eventos de tus flujos.
                  </div>
                </div>
              ) : (
                visibles.map((n) => {
                  const meta = TIPO_META[n.tipo] || TIPO_META.info
                  const Icon = meta.icon
                  return (
                    <div
                      key={n.id}
                      className={[
                        "group flex items-start gap-3 px-4 py-3 border-b border-surface-100 last:border-0 transition-colors cursor-pointer",
                        n.leida ? "bg-white hover:bg-surface-50" : "bg-brand-50/40 hover:bg-brand-50",
                      ].join(" ")}
                      onClick={() => handleClick(n)}
                    >
                      <div className={[
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                        meta.bg,
                      ].join(" ")}>
                        <Icon className={`w-4 h-4 ${meta.cls}`} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-surface-900 leading-tight truncate">
                              {n.titulo}
                            </div>
                            {n.descripcion && (
                              <div className="text-xs text-surface-500 mt-0.5 truncate">
                                {n.descripcion}
                              </div>
                            )}
                            <div className="text-[10px] text-surface-400 mt-1">
                              {haceCuanto(n.ts)}
                            </div>
                          </div>
                          {!n.leida && (
                            <span className="w-2 h-2 rounded-full bg-brand flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleRead(n.id)
                        }}
                        title={n.leida ? "Marcar como no leída" : "Marcar como leída"}
                        className={[
                          "w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all",
                          n.leida
                            ? "text-surface-300 hover:text-brand-500 hover:bg-brand-50"
                            : "text-brand-600 bg-brand-50 hover:bg-brand-100",
                        ].join(" ")}
                      >
                        {n.leida ? (
                          <Circle className="w-3.5 h-3.5" strokeWidth={2.2} />
                        ) : (
                          <Check className="w-3.5 h-3.5" strokeWidth={2.6} />
                        )}
                      </button>
                    </div>
                  )
                })
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && unreadCount > 0 && (
              <div className="border-t border-surface-100 px-4 py-2 bg-surface-50/50">
                <button
                  onClick={markAllAsRead}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 py-1.5 rounded-md hover:bg-brand-50 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Marcar todas como leídas
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
